import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createManualUser } from '../../../../api/apiFunction/userServices';
import { getAllPartners } from '../../../../api/apiFunction/partnerServices';
import { toast } from 'react-toastify';

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    accountType: 'sponsored',
    organization: '',
    partnerId: '',
    sendInvitation: false,
    role: 'individual'
  });
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  // Fetch approved partners when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchApprovedPartners();
    }
  }, [isOpen]);

  const fetchApprovedPartners = async () => {
    setPartnersLoading(true);
    try {
      const response = await getAllPartners(); // Get all partners
      console.log("Partners API response:", response);
      
      // Handle different response structures
      let partnersData = [];
      if (response?.data?.partners) {
        partnersData = response.data.partners;
      } else if (response?.data) {
        partnersData = response.data;
      } else if (Array.isArray(response)) {
        partnersData = response;
      }
      
      // Filter only non-expired partners
      const activePartners = Array.isArray(partnersData) 
        ? partnersData.filter(partner => !partner.isExpired)
        : [];
      
      console.log("activePartners", activePartners);
      setPartners(activePartners);
    } catch (error) {
      console.error('Error fetching partners:', error);
      // Don't show error toast - just set empty array
      setPartners([]);
    } finally {
      setPartnersLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for organization dropdown
    if (name === 'organization') {
      const selectedPartner = partners.find(p => p._id === value);
      setFormData(prev => ({
        ...prev,
        organization: selectedPartner ? selectedPartner.name : '',
        partnerId: value
      }));
    } else if (name === 'accountType') {
      // When switching to regular, set default organization to "Admin Sponsored"
      setFormData(prev => ({
        ...prev,
        accountType: value,
        organization: value === 'regular' ? 'Admin Sponsored' : prev.organization,
        partnerId: value === 'regular' ? '' : prev.partnerId
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Email, name, and password are required');
      return;
    }

    setLoading(true);
    try {
      const response = await createManualUser(formData);
      
      if (response?.status === 201 || response?.status === 200) {
        toast.success('User created successfully!');
        onUserCreated();
        handleClose();
      } else {
        toast.error(response?.data?.message || 'Failed to create user');
      }
    } catch (error) {
      toast.error('An error occurred while creating user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      accountType: 'sponsored',
      organization: '',
      partnerId: '',
      sendInvitation: false,
      role: 'individual'
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-bg-60 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-bd-50">
          <h2 className="text-xl font-semibold text-fg-90">Create Manual User</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-bg-50 rounded-full transition-colors"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-fg-60 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
              placeholder="student@university.edu"
              required
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-fg-60 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
              placeholder="John Doe"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-fg-60 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
              placeholder="Password123"
              required
            />
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-fg-60 mb-1">
              Account Type
            </label>
            <select
              name="accountType"
              value={formData.accountType}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
            >
              <option value="sponsored">Sponsored</option>
              <option value="regular">Regular</option>
            </select>
          </div>

          {/* Organization - Show for both Sponsored and Regular accounts */}
          {formData.accountType === 'sponsored' && (
            <div>
              <label className="block text-sm font-medium text-fg-60 mb-1">
                Organization (Partner)
              </label>
              <select
                name="organization"
                value={formData.partnerId}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
                disabled={partnersLoading}
              >
                <option value="">Select Partner Organization</option>
                {partnersLoading ? (
                  <option value="">Loading partners...</option>
                ) : (
                  partners.map((partner) => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name}
                    </option>
                  ))
                )}
              </select>
              {partners.length === 0 && !partnersLoading && (
                <p className="text-xs text-fg-60 mt-1">No active partners available</p>
              )}
            </div>
          )}

          {/* Organization - Show for Regular accounts with Admin Sponsored */}
          {formData.accountType === 'regular' && (
            <div>
              <label className="block text-sm font-medium text-fg-60 mb-1">
                Organization
              </label>
              <select
                name="organization"
                value={formData.organization}
                onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value, partnerId: '' }))}
                className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
              >
                <option value="Admin Sponsored">Admin Sponsored</option>
              </select>
            </div>
          )}

          {/* Send Invitation */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-fg-60">
              Send invitation email to user
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="sendInvitation"
                checked={formData.sendInvitation}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-bg-40 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ac-02 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ac-02"></div>
            </label>
          </div>

          {/* Role (hidden, always individual) */}
          <input type="hidden" name="role" value="individual" />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-fg-60 bg-bg-50 rounded-md hover:bg-bg-40 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-ac-02 rounded-md hover:bg-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
